// react
// dependencies
import React, { useEffect, useMemo, useState } from "react";
import { z } from "zod";

import { faExclamationCircle } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// components
import Button from "../../../../components/Button";
import DeletableChip from "../../../../components/Chip/templates/DeletableChip";
import InputText from "../../../../components/Inputs/InputText";
import LongText from "../../../../components/Inputs/LongText";
import { RadioButtons } from "../../../../components/Inputs/SelectionOptions/RadioButtons";
import Modal from "../../../../components/Modal";

// hooks
import useForm from "../../../../hooks/useForm";

// config
import { staticReportTypes } from "../../config";
// styles
import css from "./ReportInfoModal.module.scss";

export interface ModalInfo {
	name: string;
	description?: string;
	tags: string[];
	type: string;
	sharedWith?: string[];
	pageSize: number;
}

interface BaseProps {
	title?: string;
	handleClose: () => void;
	actionText: string;
	handleAction: (modalInfo: ModalInfo) => void;
	actionError?: string;
	info?: ModalInfo;
	actionColor?: "green" | "blue" | "red" | "gray-hollow";
	createReportPermissions?: {
		reviewReport: boolean;
		employeeReport: boolean;
		goalReport: boolean;
		surveyReport: boolean;
	};
	goalReportsEnabled: boolean;

	/**
	 * This is the list of available fields than can be rendered in the modal
	 */
	availableFields?: ("name" | "description" | "type" | "tags")[];
}

const defaultInfo = {
	name: "",
	description: "",
	tags: [],
	type: "",
	sharedWith: [],
};

const formSchema = z.object({
	type: z.string(),
	name: z.string().min(1, { message: "Name is required" }),
	description: z.string().optional(),
	tags: z.array(z.string()),
	sharedWith: z.array(z.string()).optional(),
});

const ReportInfoModal = (props: BaseProps) => {
	const {
		title,
		actionError,
		actionText,
		handleClose,
		handleAction,
		info = defaultInfo,
		actionColor = "green",
		createReportPermissions,
		availableFields = ["name", "description", "type", "tags"],
	} = props;

	// state
	const [tagInput, setTagsInput] = useState("");
	const [displayError, setDisplayError] = useState(actionError ? true : false);

	const { state, updateForm, validate, setInitialState } = useForm({
		schema: formSchema,
		disableReactWarning: true,
	});

	const validation = useMemo(() => validate(), [state]);

	// temporary, once feature flag removed, update report types to check for permissions
	const reportTypes = staticReportTypes.filter(
		(type) =>
			(createReportPermissions && createReportPermissions[type.value]) || (info.type && info.type === type.value)
	);

	useEffect(() => {
		setInitialState({
			...info,
			type: info.type || reportTypes[0]?.value,
		});
	}, []);

	const handleChange = (name: string, value: string | string[]) => {
		updateForm({
			[name]: value,
		});
	};

	// Add a tag to the list of tags
	const handleAddTag = () => {
		if (tagInput && state.tags.indexOf(tagInput) < 0) {
			const newTagsList = [...state.tags, tagInput];
			handleChange("tags", newTagsList);
			setTagsInput("");
		}
	};

	// remove a tag from the list of tags
	const handleDeleteTag = (tagName: string) => {
		const newTagsList = state.tags.filter((tag) => tag !== tagName);
		handleChange("tags", newTagsList);
	};

	const handleSubmitForm = () => {
		if (!validation.isValid) {
			setDisplayError(true);
			return;
		}
		handleAction(state);
	};

	const content = (
		<div className={css.modalContainer} data-testid="report-info-modal">
			<div className={css.modalHeader}>
				<div className={css.modalTitle}>{title}</div>
			</div>
			{displayError ? (
				<div className={css.errorContainer}>
					<div className={css.exclamationIcon}>
						<FontAwesomeIcon icon={faExclamationCircle} />
					</div>
					<div>{!validation.isValid ? Object.values(validation.errors)[0].message : actionError}</div>
				</div>
			) : null}

			<div className={css.inputContainer}>
				<RadioButtons
					onChange={({ value }) => handleChange("type", value)}
					options={reportTypes}
					value={{ value: state.type, label: state.type }}
					label="Report Type"
				/>
			</div>

			{availableFields.includes("name") ? (
				<div className={css.inputContainer}>
					<InputText
						label="Report Name"
						value={state.name}
						onChange={(value) => handleChange("name", value)}
						placeholder="Report name"
					/>
				</div>
			) : null}

			{availableFields.includes("description") ? (
				<div className={css.inputContainer}>
					<LongText
						label="Description"
						data-testid="description"
						value={state.description}
						onChange={(value) => handleChange("description", value)}
						style={{ minHeight: 80, fontWeight: "normal" }}
						placeholder="Description"
					/>
				</div>
			) : null}

			{availableFields.includes("tags") ? (
				<div className={css.inputContainer}>
					<div className={css.primaryLabel}>Tags:</div>
					<InputText
						placeholder="Press 'enter' to add a tag"
						value={tagInput}
						onChange={(value) => setTagsInput(value)}
						onEnter={handleAddTag}
						ariaLabel="Add report tag"
					/>
					<div className={css.tagsChips}>
						{state.tags?.map((tag, index) => (
							<DeletableChip
								key={`${tag}-${index}`}
								onDelete={() => handleDeleteTag(tag)}
								color="blue"
								label={tag}
							/>
						))}
					</div>
				</div>
			) : null}
		</div>
	);

	const buttons = (
		<div className={css.modalButtons} data-testid="close-modal">
			<Button
				size="large"
				color={actionColor !== "gray-hollow" ? actionColor : "gray"}
				isHollow={actionColor === "gray-hollow"}
				onClick={handleSubmitForm}
			>
				{actionText}
			</Button>
		</div>
	);

	return <Modal size="large" handleClose={handleClose} buttons={buttons} content={content} />;
};

export default ReportInfoModal;
